import { parseUTCFromAPI } from '@/composable/useFormatDateAndHours'
import type { FilterValueType } from '@/interfaces/components/searchBar/dynamicFilter'
import { formatApiDateTimeLocal, fromDatetimeLocal } from '@/utils/date/date-time'
import { format } from 'date-fns'

export const ALARM_LISTING_DATE_FIELDS = ['start_date', 'end_date'] as const

const DATETIME_LOCAL_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/
const ISO_WITH_OFFSET_RE = /Z|[+-]\d{2}:?\d{2}$/
const BR_DATETIME_RE = /^(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2})$/

export function normalizeAlarmListingFilterDateInput(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''

  if (ISO_WITH_OFFSET_RE.test(trimmed)) {
    return trimmed
  }

  if (DATETIME_LOCAL_RE.test(trimmed)) {
    return trimmed.replace(' ', 'T').slice(0, 16)
  }

  const brMatch = trimmed.match(BR_DATETIME_RE)
  if (brMatch) {
    const [, day, month, year, hours, minutes] = brMatch
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  if (trimmed.includes('T') || trimmed.includes(' ')) {
    const normalized = trimmed.replace(' ', 'T')
    if (DATETIME_LOCAL_RE.test(normalized)) {
      return normalized.slice(0, 16)
    }
  }

  return trimmed
}

export function isValidAlarmListingFilterDateValue(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false

  if (ISO_WITH_OFFSET_RE.test(trimmed)) {
    return formatApiDateTimeLocal(trimmed) !== 'N/A'
  }

  const normalized = normalizeAlarmListingFilterDateInput(trimmed)
  if (!normalized || !DATETIME_LOCAL_RE.test(normalized)) return false

  return !Number.isNaN(fromDatetimeLocal(normalized).getTime())
}

export function isAlarmListingDateField(field: string): boolean {
  return (ALARM_LISTING_DATE_FIELDS as readonly string[]).includes(field)
}

export function resolveAlarmListingFilterValueType(
  field: string,
  valueType?: FilterValueType,
): FilterValueType {
  if (valueType) return valueType
  if (isAlarmListingDateField(field)) return 'date'
  return 'string'
}

export function formatAlarmListingFilterDateForChip(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''

  if (ISO_WITH_OFFSET_RE.test(trimmed)) {
    const formatted = formatApiDateTimeLocal(trimmed)
    if (formatted !== 'N/A') return formatted
  }

  const normalized = normalizeAlarmListingFilterDateInput(trimmed)
  const candidate = normalized || trimmed

  if (DATETIME_LOCAL_RE.test(candidate)) {
    const localDate = fromDatetimeLocal(candidate)
    if (!Number.isNaN(localDate.getTime())) {
      return format(localDate, 'dd/MM/yyyy HH:mm')
    }
  }

  if (candidate.includes('T')) {
    const formatted = formatApiDateTimeLocal(candidate)
    if (formatted !== 'N/A') return formatted
  }

  const fallbackDate = fromDatetimeLocal(candidate)
  if (!Number.isNaN(fallbackDate.getTime())) {
    return format(fallbackDate, 'dd/MM/yyyy HH:mm')
  }

  return trimmed
}

export function alarmListingFilterDateToUtcIso(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed

  if (ISO_WITH_OFFSET_RE.test(trimmed)) {
    const parsed = parseUTCFromAPI(trimmed)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString()
    }
  }

  const normalized = normalizeAlarmListingFilterDateInput(trimmed)
  return fromDatetimeLocal(normalized || trimmed).toISOString()
}

export function normalizeAlarmListingDynamicFilterValue(
  field: string,
  value: string | number | boolean,
  valueType?: FilterValueType,
): string | number | boolean {
  const resolvedType = resolveAlarmListingFilterValueType(field, valueType)
  if (resolvedType === 'date') {
    return normalizeAlarmListingFilterDateInput(String(value)) || String(value)
  }
  return value
}
